import type { ReactNode } from 'react'
import { TaxCategories } from '../../../types/mutual-fund-detail.types'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { GrandFathering } from '../../BottomSheetComponents/mf-l2/GrandFathering'
import { GRAND_FATHERING_HEADING_ID } from '../../BottomSheetComponents/mf-l2/grandFatheringConstants'
import { IndicativeTax } from '../../BottomSheetComponents/mf-l2/IndicativeTax'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { GROUPED_STATIC_TEXT_ROLE } from '../../ui-components/NameValueTableRow'

const TAXES_BODY_TEXT_CLASS = 'text-[#6a6e6e] text-[14px] font-[400] leading-[150%]'

const GRANDFATHERING_NOTE_BEFORE_LINK =
  'LTCG up to ₹1.25L per year is exempt from tax. Gains made before 31 Jan 2018 are tax-exempt under the'

const NO_TAX_INFO_LABEL = "We don't have tax information for this fund yet."

type TaxesBodyProps = {
  taxCategory: `${TaxCategories}`
}

export const TaxesBody = ({ taxCategory }: TaxesBodyProps) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { data } = useMetaDataContext()

  const openGrandfatheringSheet = () => {
    openBottomSheet(<GrandFathering onDismiss={() => closeBottomSheet()} ScreenName='MF_L2' />, undefined, {
      drawerTitleId: GRAND_FATHERING_HEADING_ID
    })
  }

  const openIndicativeTaxSheet = (fundOrientation: 'equity-oriented' | 'debt-oriented' | 'non-equity') => {
    openBottomSheet(
      <IndicativeTax
        onDismiss={() => closeBottomSheet()}
        ScreenName='MF_L2'
        fundCategory={data?.fundDetails?.category ?? 'Such'}
        fundOrientation={fundOrientation}
      />
    )
  }

  if (taxCategory === TaxCategories.EQUITY || taxCategory === TaxCategories.ELSS) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesInfoBlock
          title='Short Term Capital Gains (STCG)'
          description='20% tax on returns if redeemed before 1 year'
        />
        <TaxesInfoBlock
          title='Long Term Capital Gains (LTCG)'
          description='12.5% tax on returns if redeemed after 1 year'
        />
        <TaxesGrandfatheringNote onGrandfatheringClick={openGrandfatheringSheet} />
      </div>
    )
  }

  if (taxCategory === TaxCategories.GIFT_PPFAS) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesInfoBlock
          title='Short Term Capital Gains (STCG)'
          description='Gains taxed at up to 42.7% if redeemed within 2 years. The fund pays this tax, so you pay nothing extra.'
        />
        <TaxesInfoBlock
          title='Long Term Capital Gains (LTCG)'
          description='Gains taxed at up to 15% if redeemed after 2 years. The fund pays this tax, so you pay nothing extra.'
        />
      </div>
    )
  }

  if (taxCategory === TaxCategories.DEBT) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesInfoBlock
          title='For units purchased before 1st Apr 2023'
          description='Gains taxed at a flat 12.5%'
        />
        <TaxesInfoBlock
          title='For units purchased on or after 1st Apr 2023'
          description='All gains are taxed as per your income tax slab.'
        />
      </div>
    )
  }

  if (taxCategory === TaxCategories.NON_EQUITY) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesInfoBlock
          title='Short Term Capital Gains (STCG)'
          description='Gains taxed as per your income tax slab if redeemed within 2 years'
        />
        <TaxesInfoBlock
          title='Long Term Capital Gains (LTCG)'
          description='Gains taxed at a flat 12.5% if redeemed after 2 years.'
        />
      </div>
    )
  }

  if (taxCategory === TaxCategories.HYBRID_EQUITY) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesIndicativeTaxIntro
          fundOrientation='equity-oriented'
          onLearnMore={() => openIndicativeTaxSheet('equity-oriented')}
        />
        <TaxesInfoBlock
          title='Short Term Capital Gains (STCG)'
          description='20% tax on returns if redeemed before 1 year'
        />
        <TaxesInfoBlock
          title='Long Term Capital Gains (LTCG)'
          description='12.5% tax on returns if redeemed after 1 year'
        />
        <TaxesGrandfatheringNote onGrandfatheringClick={openGrandfatheringSheet} />
      </div>
    )
  }

  if (taxCategory === TaxCategories.HYBRID_DEBT) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesIndicativeTaxIntro
          fundOrientation='debt-oriented'
          onLearnMore={() => openIndicativeTaxSheet('debt-oriented')}
        />
        <TaxesInfoBlock
          title='For units purchased before 1st Apr 2023'
          description='Gains taxed at a flat 12.5%'
        />
        <TaxesInfoBlock
          title='For units purchased on or after 1st Apr 2023'
          description='All gains are taxed as per your income tax slab.'
        />
      </div>
    )
  }

  if (taxCategory === TaxCategories.HYBRID_NON_EQUITY) {
    return (
      <div className='flex flex-col gap-4'>
        <TaxesIndicativeTaxIntro
          fundOrientation='non-equity'
          onLearnMore={() => openIndicativeTaxSheet('non-equity')}
        />
        <TaxesInfoBlock
          title='Short Term Capital Gains (STCG)'
          description='Gains taxed as per your income tax slab if redeemed within 2 years'
        />
        <TaxesInfoBlock
          title='Long Term Capital Gains (LTCG)'
          description='Gains taxed at a flat 12.5% if redeemed after 2 years.'
        />
      </div>
    )
  }

  return <TaxesGroupedText label={NO_TAX_INFO_LABEL} />
}

export function TaxesInfoBlock({ title, description }: { title: string; description: string }) {
  return (
    <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={`${title}, ${description}`}>
      <div aria-hidden='true'>
        <span className='text-[#232323] text-[14px] font-[500] leading-[150%]'>{title}</span>
        <br />
        <span className={`${TAXES_BODY_TEXT_CLASS} mt-1`}>{description}</span>
      </div>
    </div>
  )
}

export function TaxesGroupedText({ label }: { label: string }) {
  return (
    <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={label}>
      <span aria-hidden='true' className={TAXES_BODY_TEXT_CLASS}>
        {label}
      </span>
    </div>
  )
}

export function TaxesGrandfatheringNote({ onGrandfatheringClick }: { onGrandfatheringClick: () => void }) {
  return (
    <TaxesLinkedParagraph
      groupedAriaLabel={GRANDFATHERING_NOTE_BEFORE_LINK}
      onLinkClick={onGrandfatheringClick}
      linkAriaLabel='Grandfathering rule'
      linkText='grandfathering rule.'
    >
      LTCG up to ₹1.25L per year is exempt from tax. Gains made before 31 Jan 2018 are tax-exempt under the{' '}
    </TaxesLinkedParagraph>
  )
}

export function TaxesIndicativeTaxIntro({
  fundOrientation,
  onLearnMore
}: {
  fundOrientation: string
  onLearnMore: () => void
}) {
  const introLabel = `This fund is likely to be taxed as ${fundOrientation} fund, based on its asset allocation during the current financial year.`

  return (
    <TaxesLinkedParagraph
      groupedAriaLabel={introLabel}
      onLinkClick={onLearnMore}
      linkAriaLabel='Learn more'
      linkText='Learn more'
      linkClassName='text-[#6a6e6e] text-[14px] font-[400] underline'
    >
      This fund is <span className='font-[700]'> likely </span> to be taxed as{' '}
      <span className='font-[700]'> {fundOrientation} </span> fund, based on its asset allocation during the current
      financial year.{' '}
    </TaxesLinkedParagraph>
  )
}

export function TaxesLinkedParagraph({
  groupedAriaLabel,
  onLinkClick,
  linkAriaLabel,
  linkText,
  linkClassName = 'underline',
  children
}: {
  groupedAriaLabel: string
  onLinkClick: () => void
  linkAriaLabel: string
  linkText: string
  linkClassName?: string
  children: ReactNode
}) {
  return (
    <div className={TAXES_BODY_TEXT_CLASS}>
      <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={groupedAriaLabel} className='inline'>
        <span aria-hidden='true'>{children}</span>
      </div>
      <div onClick={onLinkClick} aria-label={linkAriaLabel} className={`${linkClassName} inline cursor-pointer`}>
        <span aria-hidden='true'>{linkText}</span>
      </div>
    </div>
  )
}
